import { GraphComponent, HtmlCanvasVisual, SvgExport } from '@ardoq/yfiles';
import { EXPORT_MARGIN_PX } from './consts';
import { collectRenderItems } from './renderGraphComponentToCanvas';
import {
  type RecordedSvgOptions,
  composeRecordedSvgDocument,
  recordSvg,
} from './recordedSvgDocument';

/**
 * renders a fully canvas-rendered ({@link HtmlCanvasVisual}) graph to a true
 * vector SVG string, by running each visual's `render(ctx)` against a
 * {@link recordSvg} recorder instead of a real 2D canvas.
 *
 * Node shapes, edges and text become real SVG; fonts (incl.
 * FontAwesome icons) and remote images are inlined so the file is self-contained,
 * the legend is appended to the right, and the canvas is sized tightly to the
 * rendered content.
 *
 * @returns the SVG document string, or null if the graph contains a non-canvas
 * visual (in which case the caller should fall back to the SVG export path).
 */
export const renderGraphComponentToSvg = async (
  graphComponent: GraphComponent,
  scale: number,
  options: RecordedSvgOptions
): Promise<string | null> => {
  const { graph } = graphComponent;
  const world = graphComponent.contentBounds;
  const items = collectRenderItems(graph);

  const exporter = new SvgExport(world, scale);
  exporter.zoom = 1;
  const renderContext = exporter.createRenderContext(graphComponent);

  const { recorder, renderable } = await recordSvg(ctx => {
    ctx.setTransform(
      scale,
      0,
      0,
      scale,
      EXPORT_MARGIN_PX - world.x * scale,
      EXPORT_MARGIN_PX - world.y * scale
    );

    const renderable = items.every(item => {
      const visual = item.getCreator().createVisual(renderContext);
      if (visual === null) {
        return true;
      }
      if (!(visual instanceof HtmlCanvasVisual)) {
        return false;
      }
      ctx.save();
      visual.render(renderContext, ctx);
      ctx.restore();
      return true;
    });

    // yFiles visuals hold no animation state, so a pass that rendered at all
    // rendered a final frame.
    return { renderable, isComplete: true };
  });

  if (!renderable) {
    return null;
  }

  return composeRecordedSvgDocument(
    recorder,
    {
      width: world.width * scale + 2 * EXPORT_MARGIN_PX,
      height: world.height * scale + 2 * EXPORT_MARGIN_PX,
    },
    options
  );
};
